import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, Shield, X, MoreHorizontal } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { Tables } from "@/integrations/supabase/types";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useNavigate } from "react-router-dom";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Pick<Tables<'profiles'>, 'display_name' | 'avatar_url' | 'subscription_status'> | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async (userId: string) => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("display_name, avatar_url, subscription_status")
          .eq("id", userId)
          .single();

        if (data) {
          setProfile(data);
        }

        // Check admin role
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .eq("role", "admin")
          .maybeSingle();

        setIsAdmin(!!roles);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };

    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUser(user);
          fetchProfile(user.id);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between pl-1 pr-2 sm:px-4 lg:px-6 w-full max-w-7xl mx-auto gap-2">
        <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center space-x-6">
          <NavLink to="/blog" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            Blog
          </NavLink>
          <NavLink to="/firehose" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            Firehose
          </NavLink>
          <NavLink to="/hot-drama" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            Hot Drama
          </NavLink>
          <NavLink to="/resources" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            Resources
          </NavLink>
          {user && profile?.subscription_status === 'active' && (
            <NavLink to="/for-you" className={({isActive}) => `text-sm font-medium transition-colors flex items-center gap-1 ${isActive ? 'text-primary font-semibold' : 'text-primary/80 hover:text-primary'}`}>
              For You ✨
            </NavLink>
          )}

          {/* Full nav items - shown at xl and above */}
          <NavLink to="/authors" className={({isActive}) => `hidden xl:block text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            Authors
          </NavLink>
          <NavLink to="/about" className={({isActive}) => `hidden xl:block text-sm font-medium transition-colors ${isActive ? 'text-primary font-semibold' : 'text-foreground/80 hover:text-foreground'}`}>
            About
          </NavLink>
          
          {/* More dropdown - shown at lg but hidden at xl */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="xl:hidden h-auto px-2 py-1 hover:bg-accent">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 bg-background/95 backdrop-blur-sm">
              <DropdownMenuItem asChild>
                <Link to="/authors" className="cursor-pointer">
                  Authors
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/about" className="cursor-pointer">
                  About
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/spill-tea" className="hidden md:block">
            <Button variant="outline" size="sm">
              Spill Tea ☕
            </Button>
          </Link>
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="hidden md:flex items-center gap-2 h-auto py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback>{getInitials(profile?.display_name || user.email || "U")}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{profile?.display_name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  Profile
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" className="hidden md:block">
              <Button variant="default" size="sm" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90">
                Sign In
              </Button>
            </Link>
          )}
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden hover:bg-primary/10 flex-shrink-0"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Open menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      <Drawer open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DrawerContent className="h-[90vh] rounded-t-2xl">
          <DrawerHeader className="border-b border-border/40 pb-4">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-2xl font-bold gradient-text">Navigation</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10">
                  <X className="h-6 w-6" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          
          <nav className="flex flex-col p-6 space-y-2 overflow-y-auto">
            <Link 
              to="/blog" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
            <Link 
              to="/firehose" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              Firehose
            </Link>
            <Link 
              to="/hot-drama" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              Hot Drama
            </Link>
            <Link 
              to="/resources" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              Resources
            </Link>
            {user && profile?.subscription_status === 'active' && (
              <Link 
                to="/for-you" 
                className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
                onClick={() => setIsMenuOpen(false)}
              >
                For You ✨
              </Link>
            )}
            <Link 
              to="/authors" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              Authors
            </Link>
            <Link 
              to="/about" 
              className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            
            <div className="pt-6 space-y-4 border-t border-border/40 mt-4">
              <Link to="/spill-tea" onClick={() => setIsMenuOpen(false)}>
                <Button variant="outline" className="w-full justify-center py-7 text-lg font-semibold rounded-xl hover:bg-primary/10 active:scale-[0.98] transition-all">
                  Spill Tea ☕
                </Button>
              </Link>
              
              {user ? (
                <>
                  <div className="flex items-center gap-3 py-4 px-5 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
                    <Avatar className="h-12 w-12 ring-2 ring-primary/20">
                      <AvatarImage src={profile?.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white">{getInitials(profile?.display_name || user.email || "U")}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-base font-semibold">{profile?.display_name || user.email}</span>
                      <span className="text-sm text-muted-foreground">Signed in</span>
                    </div>
                  </div>
                  
                  <Link 
                    to="/profile" 
                    className="flex items-center py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="flex items-center gap-2 py-4 px-5 text-lg font-medium rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/10 active:scale-[0.98] transition-all" 
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Shield className="h-5 w-5" />
                      Admin Dashboard
                    </Link>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-center py-7 text-lg font-semibold rounded-xl hover:bg-destructive/10 hover:text-destructive active:scale-[0.98] transition-all" 
                    onClick={() => {
                      handleSignOut();
                      setIsMenuOpen(false);
                    }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="default" className="w-full justify-center py-7 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary to-secondary hover:opacity-90 active:scale-[0.98] transition-all">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        </DrawerContent>
      </Drawer>
    </header>
  );
};
